// PastCanvasListModal — 過去キャンバス一覧モーダル (Slice 2、
// past-canvas-new-document-2026-07-05.md §4.2 / §7 +
// vandalism-recovery-ui-2026-09-27.md §2「入口を『続きを描く』に統一」)。
//
// 全消しで orphan 化した過去セッションの索引 (`room_past_canvases`) を新しい順に
// 並べ、各アイテムにサムネ (オンデマンド合成) + 全消し日時 + 「絵ログを共有」を出す。
// オーナー限定 (§4.2): 入口ボタンは全員に見せるが、実 fetch は Bearer admin_token
// でゲート (= 非オーナーはそもそもこのモーダルを開けず、開けたら 401 → 誘導表示)。
//
// サムネはオンデマンド (§7 「一覧を開いてスクロールで可視化した時に署名 URL を取得」):
//   - IntersectionObserver でアイテムが可視になったら `snapshot-urls` を叩き、
//     SnapshotLoader で合成 → 縮小 (`composePastCanvasThumbnail`)。
//   - 結果は pastCanvasId 単位でメモリキャッシュ (再スクロールで再合成しない)。
//   - 署名 URL 失効 (403) は 1 回だけ再署名リトライ。`SNAPSHOT_PUBLISHER=s3` でない
//     dev 環境は snapshot-urls が 404 → プレースホルダに degrade (一覧自体は出る)。
//
// 「絵ログを共有」は既存 `TimelapseShareModal` を、その過去キャンバスの `snapshotSeq`
// を終端 (`initialEndSeq`) にして開く (§4.5-3)。スライダー可動域はその時点の
// **系譜連結ドメイン** (§4.7 `buildTimelapseSliderDomain`) — 過去キャンバスが
// 復元で始まる場合は復元元の系譜も可動域に入る (従来の「その文書のみ」から拡大)。
//
// **入口統一 (2026-09-28)**: 別ボタン「時点を選んで戻す」は廃止。各行の「続きを
// 描く」は、その行の文書 (`row.sessionId`) にスコープした `RecoveryGenerationPickerModal`
// を開く (即 `whiteboard.restore-request` は送らない — 比較画面を必ず経由する、
// §1.4 の取り違え対策)。一覧の先頭には常に「今の絵」カードを出し (過去キャンバス
// 0 件でも)、同じ画面を現行文書スコープで開く。ゲートは `Perm.CLEAR` のみ
// (`canRestorePastCanvas`、clearOpen バイパス不適用 §4.2)。
//
// `?recover=<seq>` 深リンク (`initialRecoverSeq` prop): 一覧が確定したら 1 回だけ
// `pickRecoverTargetRow` で対象行を決め、自動でその選択画面を開く (無ければ今の絵)。
//
// 同じ文書 (`sessionId`) を指す複数行には同じ印 (`assignSessionMarks`) を付ける。
//
// engine / drawing には触れない。モーダル枠は `modalChrome.ts` の共通
// ヘルパ (TimelapseShareModal と同じ視覚言語)。

import { useCallback, useEffect, useId, useRef, useState } from 'react';

import {
  canRestorePastCanvas,
  formatPastCanvasClearedLabel,
} from '../application/pastCanvasList.js';
import type { Transport } from '../application/ports/Transport.js';
import {
  assignSessionMarks,
  pastCanvasKindLabel,
  pickRecoverTargetRow,
  type RecoveryPickerRow,
} from '../application/recoveryGenerations.js';
import { readEnv } from '../infrastructure/env.js';
import {
  deletePastCanvas,
  fetchPastCanvasList,
  fetchPastCanvasSnapshotUrls,
  type PastCanvasListItem,
} from '../infrastructure/pastCanvas/pastCanvasClient.js';
import { composePastCanvasThumbnail } from '../infrastructure/pastCanvas/pastCanvasThumbnail.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { timelapseModalChrome } from './modalChrome.js';
import { RecoveryGenerationPickerModal } from './RecoveryGenerationPickerModal.js';
import { TimelapseShareModal } from './TimelapseShareModal.js';

/** サムネの長辺 (px)。一覧グリッドのタイルに収まる程度に小さく合成する。 */
const THUMBNAIL_MAX_EDGE = 240;

/** 同じ文書の印に使う固定パレット (`assignSessionMarks` の index を `% length` で当てる)。 */
const SESSION_MARK_COLORS = [
  '#f59e0b', // amber
  '#38bdf8', // sky
  '#a78bfa', // violet
  '#fb7185', // rose
  '#34d399', // emerald
] as const;

// ── 一覧取得 state ─────────────────────────────────────────────────────────

type ListState =
  | { readonly t: 'loading' }
  | { readonly t: 'ok'; readonly items: readonly PastCanvasListItem[] }
  | { readonly t: 'empty' }
  | { readonly t: 'error'; readonly message: string };

const LIST_ERROR_MESSAGES = {
  unauthorized: 'この一覧を表示する権限がありません。オーナーとしてログインしてください',
  'not-found': 'この部屋の過去キャンバスが見つかりませんでした',
  error: '過去キャンバスの取得に失敗しました。通信環境を確認して再度お試しください',
} as const;

// 削除失敗 (401/403/5xx/network) 時のメッセージ。冪等な 404 は成功扱い (一覧から除く)。
const DELETE_ERROR_MESSAGES = {
  unauthorized: '削除する権限がありません。オーナーとしてログインし直してください',
  error: '過去キャンバスの削除に失敗しました。通信環境を確認して再度お試しください',
} as const;

// ── サムネのメモリキャッシュ (pastCanvasId → objectURL) ─────────────────────
//
// セッション内で再スクロールしても再合成しない。object URL は session 内で保持
// (最大50枚の小さな PNG。document unload で自動解放される想定)。id は UUIDv7 で
// 部屋を跨いで一意なので room を跨いだ衝突は無い。
const thumbnailUrlCache = new Map<string, string>();

// ── ボタン等の共通クラス (TimelapseShareModal の視覚言語を踏襲) ─────────────

const secondaryButtonClass =
  'rounded border border-white/30 bg-[var(--color-bg-soft)] px-3 py-1.5 text-xs text-white hover:bg-[var(--color-bg)]';

// 「続きを描く」は主アクションなので accent の primary 配色。disabled は権限無しのみ
// (復元 request 自体は選択画面に移ったので、このボタンは常に「画面を開く」だけ)。
const primaryButtonClass =
  'rounded bg-[var(--color-accent)] px-3 py-1.5 text-xs font-bold text-[var(--color-panel)] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:brightness-100';

// ── サムネ (lazy + キャッシュ + 期限再取得) ────────────────────────────────

type ThumbState =
  | { readonly t: 'idle' }
  | { readonly t: 'loading' }
  | { readonly t: 'ready'; readonly url: string }
  | { readonly t: 'placeholder' };

interface UsePastCanvasThumbnailArgs {
  readonly roomId: string;
  readonly item: PastCanvasListItem;
  readonly adminToken: string | undefined;
}

/**
 * アイテムが可視になったらサムネを 1 回だけオンデマンド合成する hook。
 * 返り値の `ref` を可視判定対象の要素に付ける。`layerCount === 0` (snapshot 無し)
 * は最初からプレースホルダに倒す (署名しても layer が無い)。
 */
const usePastCanvasThumbnail = ({
  roomId,
  item,
  adminToken,
}: UsePastCanvasThumbnailArgs): {
  ref: (el: HTMLElement | null) => void;
  state: ThumbState;
} => {
  const cached = thumbnailUrlCache.get(item.id);
  const [state, setState] = useState<ThumbState>(
    cached !== undefined
      ? { t: 'ready', url: cached }
      : item.layerCount === 0
        ? { t: 'placeholder' }
        : { t: 'idle' },
  );
  const [visible, setVisible] = useState(cached !== undefined || item.layerCount === 0);
  const elRef = useRef<HTMLElement | null>(null);
  // ロードのワンショット化。可視になった最初の 1 回だけ合成を開始する。これを
  // 依存配列で表現しようとすると、本体が呼ぶ `setState('loading')` が同じエフェクトを
  // 再トリガして前回の `ac.abort()` を即実行し、開始直後の fetch を自己キャンセルして
  // しまう (= サムネが全部「プレビューなし」に倒れる)。そこで開始済みフラグを ref で
  // 持ち、`state.t` を依存配列から外す。
  const startedRef = useRef(false);

  // IntersectionObserver: 一度可視になったら記録して監視解除 (= 1 回だけ発火)。
  const ref = useCallback((el: HTMLElement | null) => {
    elRef.current = el;
  }, []);

  useEffect(() => {
    if (visible) return;
    const el = elRef.current;
    if (el === null) return;
    if (typeof IntersectionObserver === 'undefined') {
      // 非対応環境 (= 一部の jsdom) では即可視扱いにして degrade しない。
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
          return;
        }
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible || startedRef.current) return;
    startedRef.current = true;
    // 初期 state が既に終端 (cache hit → 'ready' / layerCount===0 → 'placeholder')
    // の行はロード不要。cache と layerCount から同じ判定を再導出する (state を読まない
    // ことで `state.t` を依存配列から外し、自己キャンセルを避ける)。
    if (thumbnailUrlCache.has(item.id) || item.layerCount === 0) return;
    const ac = new AbortController();
    setState({ t: 'loading' });
    void (async () => {
      const dispatcherUrl = readEnv().dispatcherUrl;
      const signOnce = () =>
        fetchPastCanvasSnapshotUrls({
          dispatcherUrl,
          roomId,
          pastCanvasId: item.id,
          ...(adminToken !== undefined ? { adminToken } : {}),
          signal: ac.signal,
        });
      let signed = await signOnce();
      if (ac.signal.aborted) return;
      if (signed.outcome !== 'ok') {
        setState({ t: 'placeholder' });
        return;
      }
      let composed = await composePastCanvasThumbnail(signed.urls, {
        maxEdge: THUMBNAIL_MAX_EDGE,
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      // 署名 URL 失効 (403) → 1 回だけ再署名してリトライ (§7)。
      if (composed.outcome === 'expired') {
        signed = await signOnce();
        if (ac.signal.aborted) return;
        if (signed.outcome !== 'ok') {
          setState({ t: 'placeholder' });
          return;
        }
        composed = await composePastCanvasThumbnail(signed.urls, {
          maxEdge: THUMBNAIL_MAX_EDGE,
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
      }
      if (composed.outcome !== 'ok') {
        setState({ t: 'placeholder' });
        return;
      }
      const url = URL.createObjectURL(composed.blob);
      thumbnailUrlCache.set(item.id, url);
      setState({ t: 'ready', url });
    })();
    return () => ac.abort();
  }, [visible, roomId, item.id, item.layerCount, adminToken]);

  return { ref, state };
};

/** 「今の絵」カード用: `getCurrentCanvasBlob` を 1 回だけ呼んで thumbnail を作る。 */
const useCurrentDocThumbnail = (getCurrentCanvasBlob: () => Promise<Blob | null>): ThumbState => {
  const [state, setState] = useState<ThumbState>({ t: 'loading' });
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ t: 'loading' });
    void (async () => {
      const blob = await getCurrentCanvasBlob();
      if (cancelled) return;
      if (blob === null) {
        setState({ t: 'placeholder' });
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      setState({ t: 'ready', url: objectUrl });
    })();
    return () => {
      cancelled = true;
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [getCurrentCanvasBlob]);
  return state;
};

const thumbBox = (state: ThumbState): React.ReactNode => (
  <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded bg-[var(--color-bg)]">
    {state.t === 'ready' ? (
      // 装飾サムネ。意味は下のラベルが担うので alt は空 (a11y: 装飾扱い)。
      <img src={state.url} alt="" className="max-h-full max-w-full object-contain" />
    ) : state.t === 'loading' ? (
      <span
        role="status"
        aria-label="サムネイルを読み込み中"
        data-testid="past-canvas-thumb-loading"
        className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-accent)] border-t-transparent"
      />
    ) : (
      // idle / placeholder: snapshot 無し or 取得不可。控えめなプレースホルダ。
      <span
        aria-hidden="true"
        data-testid="past-canvas-thumb-placeholder"
        className="text-[10px] text-[var(--color-text-muted)]"
      >
        プレビューなし
      </span>
    )}
  </div>
);

/** 同じ文書の印 (入口統一 §2)。サムネ左上に小さな色つき点。 */
const SessionMarkDot = ({ markIndex }: { readonly markIndex: number }): React.ReactNode => (
  <span
    aria-hidden="true"
    data-testid="past-canvas-session-mark"
    className="absolute top-1 left-1 h-2.5 w-2.5 rounded-full border border-black/30"
    style={{ backgroundColor: SESSION_MARK_COLORS[markIndex % SESSION_MARK_COLORS.length] }}
  />
);

// ── 「今の絵」カード (入口統一 §2「一覧の先頭に常に表示」) ───────────────────

interface CurrentDocCardProps {
  readonly getCurrentCanvasBlob: () => Promise<Blob | null>;
  readonly canRestore: boolean;
  readonly onContinue: () => void;
}

const CurrentDocCard = ({
  getCurrentCanvasBlob,
  canRestore,
  onContinue,
}: CurrentDocCardProps): React.ReactNode => {
  const state = useCurrentDocThumbnail(getCurrentCanvasBlob);
  return (
    <li
      data-testid="past-canvas-current-card"
      className="flex flex-col gap-1.5 rounded border border-[var(--color-accent)] bg-[var(--color-bg-soft)] p-2"
    >
      {thumbBox(state)}
      <span className="select-none text-[11px] text-[var(--color-text)]">今の絵</span>
      <button
        type="button"
        onClick={onContinue}
        disabled={!canRestore}
        title={!canRestore ? '復元できるのはオーナー (全消し権限あり) のみです' : undefined}
        className={primaryButtonClass}
      >
        続きを描く
      </button>
    </li>
  );
};

// ── 一覧アイテム ────────────────────────────────────────────────────────────

interface PastCanvasCardProps {
  readonly roomId: string;
  readonly item: PastCanvasListItem;
  readonly adminToken: string | undefined;
  readonly onShare: (item: PastCanvasListItem) => void;
  /** `Perm.CLEAR` 保有か (Slice 3 §4.2)。false なら「続きを描く」を disabled にする。 */
  readonly canRestore: boolean;
  readonly onContinue: (item: PastCanvasListItem) => void;
  /**
   * オーナー (削除可能) か (Slice 4 §4.8)。true のときだけカードにゴミ箱を出す。
   * 非オーナーはそもそも一覧を開けないが、owner 状態でガードする。
   */
  readonly canDelete: boolean;
  /** 削除 (DELETE) 進行中か。多重削除を防ぐため全カードのゴミ箱を止める。 */
  readonly deletePending: boolean;
  readonly onDelete: (item: PastCanvasListItem) => void;
  /** 同じ文書を指す他行と共有する印の index (入口統一 §2)。無ければ印を出さない。 */
  readonly sessionMarkIndex: number | undefined;
}

export const PastCanvasCard = ({
  roomId,
  item,
  adminToken,
  onShare,
  canRestore,
  onContinue,
  canDelete,
  deletePending,
  onDelete,
  sessionMarkIndex,
}: PastCanvasCardProps): React.ReactNode => {
  const { ref, state } = usePastCanvasThumbnail({ roomId, item, adminToken });
  const label = formatPastCanvasClearedLabel(item.clearedAt);
  return (
    <li
      ref={ref}
      data-testid="past-canvas-card"
      className="flex flex-col gap-1.5 rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] p-2"
    >
      <div className="relative">
        {thumbBox(state)}
        {sessionMarkIndex !== undefined && <SessionMarkDot markIndex={sessionMarkIndex} />}
        {/* ゴミ箱 (Slice 4 §4.8): オーナーのみ。サムネ右上に重ねて、下の
            ボタンスタックのレイアウトを崩さない。削除進行中は多重削除防止で disabled。 */}
        {canDelete && (
          <button
            type="button"
            aria-label="この過去キャンバスを削除"
            data-testid="past-canvas-delete"
            onClick={() => onDelete(item)}
            disabled={deletePending}
            className="absolute top-1 right-1 flex h-7 w-7 items-center justify-center rounded bg-black/50 text-white transition hover:bg-[var(--color-error)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-black/50"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 4h10M6.5 4V2.5h3V4M4.5 4l.5 9h6l.5-9M6.5 6.5v4M9.5 6.5v4" />
            </svg>
          </button>
        )}
      </div>
      <span className="select-none text-[11px] text-[var(--color-text)]">{label}</span>
      {/* 種類ラベル (荒らし復旧 UI 第 1 段階、§2 確定 4): 全消し / 復元前の自動保存 /
          時点を選んで作成。`kind === null` (カラム追加以前の既存行) は何も出さない。 */}
      {pastCanvasKindLabel(item.kind) !== null && (
        <span className="select-none text-[10px] text-[var(--color-text-muted)]">
          {pastCanvasKindLabel(item.kind)}
          {/* 外した版は何を外したかも一目で (第 2 段階)。 */}
          {item.kind === 'excluded' && item.exclusion != null
            ? ` (${item.exclusion.excludeUids.length}人${
                item.exclusion.excludeSids.length > 0
                  ? `・${item.exclusion.excludeSids.length}本`
                  : ''
              })`
            : ''}
        </span>
      )}
      {/* 「続きを描く」(入口統一): snapshot が無い行 (layerCount=0) は復元できない
          のでボタン自体を出さない。Perm.CLEAR 非保有は disabled + tooltip (一覧は
          オーナー限定なので通常到達しないが、選択画面側も再検証する保険ゲート)。
          押すと即 restore-request ではなく、この行の文書にスコープした選択画面
          (`RecoveryGenerationPickerModal`) を開く。 */}
      {item.layerCount > 0 && (
        <button
          type="button"
          onClick={() => onContinue(item)}
          disabled={!canRestore}
          title={!canRestore ? '復元できるのはオーナー (全消し権限あり) のみです' : undefined}
          className={primaryButtonClass}
        >
          続きを描く
        </button>
      )}
      <button type="button" onClick={() => onShare(item)} className={secondaryButtonClass}>
        絵ログを共有
      </button>
    </li>
  );
};

// ── モーダル本体 ────────────────────────────────────────────────────────────

/** 一覧の行 → 選択画面に渡す行情報 (第 2 段階が文書の終端と種類を使う)。 */
const toPickerRow = (item: PastCanvasListItem): RecoveryPickerRow => ({
  pastCanvasId: item.id,
  snapshotSeq: item.snapshotSeq,
  clearedAt: item.clearedAt,
  clearedSeq: item.clearedSeq,
  kind: item.kind,
});

/** 選択画面 (`RecoveryGenerationPickerModal`) をどの文書にスコープして開くか。 */
interface RecoveryTarget {
  /** 開いた行の文書。`undefined` = 現行文書 (「今の絵」カードから開いた)。 */
  readonly sessionId: string | undefined;
  readonly row: RecoveryPickerRow | undefined;
  /** `?recover=<seq>` 深リンクから自動で開いた場合の初期選択。 */
  readonly initialSeq: number | undefined;
}

export interface PastCanvasListModalProps {
  readonly roomId: string;
  /** ルームのキャンバス寸法 (絵ログ共有モーダルへ引き渡す)。 */
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  /** 絵ログ共有モーダルの live tail WS 往復に使う。 */
  readonly transport: Transport;
  /** 絵ログ共有モーダルの worker 注入 (node テスト可能性のため親から渡す)。 */
  readonly workerFactory: () => Worker;
  /** owner の admin_token (Bearer)。一覧 fetch + サムネ署名 + 絵ログ共有で使う。 */
  readonly adminToken?: string;
  /**
   * 自分の permissions ビットマスク (`RoomState.permissions`)。「続きを描く」の
   * UI ゲート (`canRestorePastCanvas` = `Perm.CLEAR` のみ、Slice 3 §4.2) に使う。
   */
  readonly permissions: number;
  /** スマホ (フォンシェル) か。フルスクリーン枠 + 絵ログ共有モーダルへ伝播。 */
  readonly isMobile?: boolean;
  /**
   * 荒らし復旧 UI 第 1 段階 (vandalism-recovery-ui-2026-09-27.md §2)「続きを描く」
   * の比較プレビュー用。`useRoomChrome.getCurrentCanvasBlob` をそのまま渡す。
   */
  readonly getCurrentCanvasBlob: () => Promise<Blob | null>;
  /** 現行文書の現在の背景色 (rgb24、`useRoomChrome.backgroundRgb`)。「人を外して戻す」用。 */
  readonly currentBackgroundRgb?: number;
  /**
   * `?recover=<seq>` 深リンク (入口統一 §2)。一覧が確定したら 1 回だけ、対象行の
   * 選択画面を自動で開く (`pickRecoverTargetRow`、無ければ今の絵)。
   */
  readonly initialRecoverSeq?: number;
  readonly onClose: () => void;
}

export const PastCanvasListModal = ({
  roomId,
  canvasWidth,
  canvasHeight,
  transport,
  workerFactory,
  adminToken,
  permissions,
  isMobile = false,
  getCurrentCanvasBlob,
  currentBackgroundRgb,
  initialRecoverSeq,
  onClose,
}: PastCanvasListModalProps): React.ReactNode => {
  const titleId = useId();
  const [listState, setListState] = useState<ListState>({ t: 'loading' });
  // 「絵ログを共有」で開く子モーダル (その過去キャンバスへスコープ)。
  const [sharing, setSharing] = useState<PastCanvasListItem | null>(null);
  // 「続きを描く」選択画面 (入口統一)。行 or 今の絵カードから開く。
  const [recoveryTarget, setRecoveryTarget] = useState<RecoveryTarget | null>(null);
  const canRestore = canRestorePastCanvas(permissions);
  // 削除フロー (Slice 4 §4.8): ゴミ箱 → 確認ダイアログ対象 → DELETE 送信中 →
  // 成功/冪等 404 で一覧から除去 / 失敗でエラー表示 (カードは残る)。
  const [deleteTarget, setDeleteTarget] = useState<PastCanvasListItem | null>(null);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // オーナー (削除可能) 判定。一覧はオーナー限定で fetch 済だが、admin_token が
  // 無ければ DELETE は 401 になるので、token があるときだけゴミ箱を出す。
  const canDelete = canRestore && adminToken !== undefined;
  // `?recover=` の自動オープンは 1 回だけ (一覧が確定するたびに再オープンしない)。
  const autoOpenAppliedRef = useRef(false);

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      const outcome = await fetchPastCanvasList({
        dispatcherUrl: readEnv().dispatcherUrl,
        roomId,
        ...(adminToken !== undefined ? { adminToken } : {}),
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      if (outcome.outcome !== 'ok') {
        setListState({ t: 'error', message: LIST_ERROR_MESSAGES[outcome.outcome] });
        return;
      }
      setListState(outcome.items.length === 0 ? { t: 'empty' } : { t: 'ok', items: outcome.items });
    })();
    return () => ac.abort();
  }, [roomId, adminToken]);

  // `?recover=<seq>` 深リンク (入口統一 §2): 一覧が確定 (ok/empty/error いずれも
  // 「これ以上待っても仕方ない」ので確定扱い) したら 1 回だけ、対象行の選択画面を
  // 自動で開く。`pickRecoverTargetRow` は `clearedSeq >= seq` の最小の行を返す —
  // 無ければ (エラーで一覧が読めない場合も含め) 今の絵を対象にする。
  useEffect(() => {
    if (initialRecoverSeq === undefined || autoOpenAppliedRef.current) return;
    if (listState.t === 'loading') return;
    autoOpenAppliedRef.current = true;
    const items = listState.t === 'ok' ? listState.items : [];
    const target = pickRecoverTargetRow(items, initialRecoverSeq);
    setRecoveryTarget(
      target === null
        ? { sessionId: undefined, row: undefined, initialSeq: initialRecoverSeq }
        : {
            sessionId: target.sessionId,
            row: toPickerRow(target),
            initialSeq: initialRecoverSeq,
          },
    );
  }, [initialRecoverSeq, listState]);

  // 一覧モーダルは軽い dismiss (backdrop / Esc)。共有子モーダル / 削除確認 /
  // 選択画面が開いている間はそちらが前面なので Esc は前面側に任せる (= 二重 close
  // を避ける)。
  useEffect(() => {
    if (sharing !== null || deleteTarget !== null || recoveryTarget !== null) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sharing, deleteTarget, recoveryTarget, onClose]);

  const handleShare = (item: PastCanvasListItem): void => {
    setSharing(item);
  };

  const handleContinueFromRow = (item: PastCanvasListItem): void => {
    setRecoveryTarget({
      sessionId: item.sessionId,
      row: toPickerRow(item),
      initialSeq: undefined,
    });
  };

  const handleContinueFromCurrent = (): void => {
    setRecoveryTarget({ sessionId: undefined, row: undefined, initialSeq: undefined });
  };

  const handleDelete = (item: PastCanvasListItem): void => {
    setDeleteError(null);
    setDeleteTarget(item);
  };

  const handleConfirmDelete = (): void => {
    if (deleteTarget === null) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    setDeletePending(true);
    void (async () => {
      const outcome = await deletePastCanvas({
        dispatcherUrl: readEnv().dispatcherUrl,
        roomId,
        pastCanvasId: target.id,
        ...(adminToken !== undefined ? { adminToken } : {}),
      });
      setDeletePending(false);
      // 成功 (204) と冪等 404 (既に無い) は同じ扱い = 当該カードを一覧から除去。
      if (outcome.outcome === 'ok' || outcome.outcome === 'not-found') {
        setListState((s) => {
          if (s.t !== 'ok') return s;
          const remaining = s.items.filter((it) => it.id !== target.id);
          return remaining.length === 0 ? { t: 'empty' } : { t: 'ok', items: remaining };
        });
        return;
      }
      // 認可 / 通信エラー: カードは残し、エラーを既存の通知流儀で提示。
      setDeleteError(DELETE_ERROR_MESSAGES[outcome.outcome]);
    })();
  };

  const sessionMarks = listState.t === 'ok' ? assignSessionMarks(listState.items) : null;

  const body = ((): React.ReactNode => {
    switch (listState.t) {
      case 'loading':
        return (
          <p role="status" className="text-sm text-[var(--color-text-muted)]">
            過去キャンバスを読み込んでいます…
          </p>
        );
      case 'empty':
        return (
          <p className="text-sm text-[var(--color-text)]">
            まだ過去キャンバスがありません。全消しをすると、それまでの絵がここに残ります。
          </p>
        );
      case 'error':
        return <p className="text-sm text-[var(--color-text)]">{listState.message}</p>;
      case 'ok':
        return (
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {listState.items.map((item) => (
              <PastCanvasCard
                key={item.id}
                roomId={roomId}
                item={item}
                adminToken={adminToken}
                onShare={handleShare}
                canRestore={canRestore}
                onContinue={handleContinueFromRow}
                canDelete={canDelete}
                deletePending={deletePending}
                onDelete={handleDelete}
                sessionMarkIndex={sessionMarks?.get(item.sessionId)}
              />
            ))}
          </ul>
        );
    }
  })();

  const deleteNotice =
    deleteError !== null ? (
      <p role="alert" className="mb-2 text-xs text-[var(--color-error)]">
        {deleteError}
      </p>
    ) : null;

  const chrome = timelapseModalChrome(isMobile);

  return (
    <div className={chrome.root}>
      {/* backdrop: 一覧は軽い dismiss (共有子モーダルが開いていないとき)。スマホは
          フルスクリーンなので backdrop を出さない (TimelapseShareModal と同流儀)。 */}
      {!isMobile &&
        (sharing === null ? (
          <button
            type="button"
            aria-label="閉じる"
            tabIndex={-1}
            onClick={onClose}
            className="-z-10 absolute inset-0 cursor-default bg-black/60"
          />
        ) : (
          <div aria-hidden="true" className="-z-10 absolute inset-0 bg-black/60" />
        ))}
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className={chrome.dialog}>
        <div className={chrome.header}>
          <h2 id={titleId} className="font-bold text-[var(--color-accent)] text-base">
            過去キャンバス
          </h2>
          <button
            type="button"
            aria-label="閉じる"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg-soft)]"
          >
            ×
          </button>
        </div>
        <div className={chrome.body}>
          {deleteNotice}
          {/* 「今の絵」カード (入口統一 §2): 過去キャンバスが 0 件でも常に出す。 */}
          <ul className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
            <CurrentDocCard
              getCurrentCanvasBlob={getCurrentCanvasBlob}
              canRestore={canRestore}
              onContinue={handleContinueFromCurrent}
            />
          </ul>
          {body}
        </div>
      </div>

      {/* 削除確認ダイアログ (Slice 4 §4.8)。実体は sweeper 回収だが、ユーザー体感は
          不可逆なので「元に戻せません」と伝える。破壊的操作なので destructive 配色。
          ConfirmDialog は z-[60] で本モーダル (z-50) の前面に出る。 */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="過去キャンバスを削除"
        message={'この過去キャンバスを削除しますか？\n元に戻せません。'}
        confirmLabel="削除"
        destructive
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* 過去キャンバスから絵ログ共有 (§4.5-3): 既存 TimelapseShareModal を、その
          過去キャンバスの snapshotSeq を終端にして開く (可動域 = その時点の系譜
          連結ドメイン、§4.7)。子モーダルは前面 (z-50)。close で一覧へ戻る (一覧は
          そのまま背後に残す)。 */}
      {sharing !== null && (
        <TimelapseShareModal
          roomId={roomId}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          transport={transport}
          workerFactory={workerFactory}
          isMobile={isMobile}
          {...(adminToken !== undefined ? { adminToken } : {})}
          initialEndSeq={sharing.snapshotSeq}
          onClose={() => setSharing(null)}
        />
      )}

      {/* 「続きを描く」選択画面 (入口統一)。行 or 今の絵カードから開く。前面 (z-50)、
          × / キャンセルで一覧へ戻る (一覧は開いたまま)。restore 成功時は自身が
          `onRestored` (= 一覧の `onClose`) を呼び一覧ごと閉じる (キャンバスの実
          差し替えは SessionProvider の hydration 経路)。 */}
      {recoveryTarget !== null && (
        <RecoveryGenerationPickerModal
          roomId={roomId}
          transport={transport}
          isMobile={isMobile}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          {...(adminToken !== undefined ? { adminToken } : {})}
          {...(recoveryTarget.sessionId !== undefined
            ? { sessionId: recoveryTarget.sessionId }
            : {})}
          {...(recoveryTarget.row !== undefined ? { row: recoveryTarget.row } : {})}
          {...(recoveryTarget.initialSeq !== undefined
            ? { initialSeq: recoveryTarget.initialSeq }
            : {})}
          getCurrentCanvasBlob={getCurrentCanvasBlob}
          {...(currentBackgroundRgb !== undefined ? { currentBackgroundRgb } : {})}
          onClose={() => setRecoveryTarget(null)}
          onRestored={onClose}
        />
      )}
    </div>
  );
};
